import React from "react";
import BarcodeCanvas from "./BarcodeCanvas";
import QrCodePreview from "./QrCodePreview";
import { resolvePlaceholderText } from "../utils/placeholderFields";
import { mmToPx } from "../utils/labelSizes";
import { normalizeNutritionRows } from "../utils/nutritionTable";

const FSSAI_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/images/FSSAI_logo%20(1).png`;

// Renders the visual content of a single label element.
// `dataMap` (optional) is the resolved placeholder map from the backend;
// when absent, raw {{placeholder}} tokens are shown so the designer stays readable.
const ElementRenderer = ({ element, dataMap }) => {
  const baseStyle = {
    width: "100%",
    height: "100%",
    opacity: element.opacity != null ? element.opacity : 1,
    boxSizing: "border-box",
    overflow: "hidden",
  };

  switch (element.type) {
    case "text": {
      const text = resolvePlaceholderText(element.content, dataMap);
      return (
        <div
          style={{
            ...baseStyle,
            fontFamily:
              element.fontFamily || "Poppins, Noto Sans, Arial, sans-serif",
            fontSize: `${element.fontSize || 12}px`,
            fontWeight: element.bold ? "bold" : element.fontWeight || "normal",
            fontStyle: element.italic ? "italic" : "normal",
            textDecoration: element.underline ? "underline" : "none",
            color: element.color || "#000000",
            textAlign: element.textAlign || "left",
            backgroundColor: element.backgroundColor || "transparent",
            border: element.borderWidth
              ? `${element.borderWidth}px solid ${element.borderColor || "#000"}`
              : "none",
            padding: `${element.padding || 0}px`,
            display: "flex",
            alignItems: element.verticalAlign || "flex-start",
            justifyContent:
              element.textAlign === "center"
                ? "center"
                : element.textAlign === "right"
                  ? "flex-end"
                  : "flex-start",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {text}
        </div>
      );
    }
    case "image": {
      const src = resolvePlaceholderText(element.src, dataMap);
      return src ? (
        <img
          src={src}
          alt=""
          crossOrigin="anonymous"
          style={{
            ...baseStyle,
            objectFit: element.objectFit || "contain",
            border: element.borderWidth
              ? `${element.borderWidth}px solid ${element.borderColor || "#000"}`
              : "none",
          }}
        />
      ) : (
        <div
          style={{
            ...baseStyle,
            border: "1px dashed #bbb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            color: "#999",
          }}
        >
          Image
        </div>
      );
    }
    case "vegIcon": {
      const rawValue =
        element.valueSource === "manual"
          ? element.manualValue || "VEG"
          : resolvePlaceholderText(
              `{{${element.fieldKey || "veg_type"}}}`,
              dataMap,
            );
      const normalizedValue = String(rawValue || "VEG")
        .trim()
        .toUpperCase()
        .replace(/[-\s]/g, "_");
      const isNonVeg = normalizedValue === "NON_VEG";
      const accentColor = isNonVeg ? "#8b3f19" : "#1e8e3e";
      const markerFill = isNonVeg ? accentColor : "#22a447";

      return (
        <div
          style={{
            ...baseStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            viewBox="0 0 100 100"
            width="100%"
            height="100%"
            aria-hidden="true"
          >
            <rect
              x="12"
              y="12"
              width="76"
              height="76"
              rx="6"
              fill="#ffffff"
              stroke={accentColor}
              strokeWidth="8"
            />
            {isNonVeg ? (
              <polygon
                points="50,26 72,70 28,70"
                fill={markerFill}
                stroke={accentColor}
                strokeWidth="4"
              />
            ) : (
              <circle cx="50" cy="50" r="20" fill={markerFill} />
            )}
          </svg>
        </div>
      );
    }
    case "fssaiBadge": {
      const value =
        element.valueSource === "manual"
          ? element.manualValue || ""
          : resolvePlaceholderText(
              `{{${element.fieldKey || "fssai"}}}`,
              dataMap,
            );

      return (
        <div
          style={{
            ...baseStyle,
            border: "1px solid #d4d4d4",
            borderRadius: 10,
            background: "#ffffff",
            padding: "4px 8px 6px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 4,
          }}
        >
          <img
            src={FSSAI_LOGO_SRC}
            alt="FSSAI"
            crossOrigin="anonymous"
            style={{
              width: "94%",
              height: "64%",
              objectFit: "contain",
              display: "block",
              margin: "0 auto",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
          <div
            style={{
              fontFamily: "Poppins, Noto Sans, Arial, sans-serif",
              fontSize: "7px",
              fontWeight: 500,
              textAlign: "center",
              color: "#5a5a5a",
              lineHeight: 1.1,
              whiteSpace: "nowrap",
            }}
          >
            Lic. No. {value || "Enter FSSAI No."}
          </div>
        </div>
      );
    }
    case "rect":
      return (
        <div
          style={{
            ...baseStyle,
            backgroundColor: element.backgroundColor || "transparent",
            border: `${element.borderWidth != null ? element.borderWidth : 1}px solid ${element.borderColor || "#000"}`,
            borderRadius: element.borderRadius || 0,
          }}
        />
      );
    case "circle":
      return (
        <div
          style={{
            ...baseStyle,
            backgroundColor: element.backgroundColor || "transparent",
            border: `${element.borderWidth != null ? element.borderWidth : 1}px solid ${element.borderColor || "#000"}`,
            borderRadius: "50%",
          }}
        />
      );
    case "line":
      return (
        <div
          style={{
            width: "100%",
            height: `${element.borderWidth || 1}px`,
            backgroundColor: element.borderColor || "#000",
            marginTop: "50%",
          }}
        />
      );
    case "barcode": {
      const value = resolvePlaceholderText(
        element.barcodeValue || "{{barcode}}",
        dataMap,
      );
      return (
        <div
          style={{
            ...baseStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BarcodeCanvas
            value={value}
            type={element.barcodeType || "CODE128"}
            height={mmToPx(element.height) - 14}
          />
        </div>
      );
    }
    case "qr": {
      const value = resolvePlaceholderText(
        element.barcodeValue || "{{qr}}",
        dataMap,
      );
      return (
        <div
          style={{
            ...baseStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <QrCodePreview
            value={value}
            size={Math.min(mmToPx(element.width), mmToPx(element.height))}
          />
        </div>
      );
    }
    case "nutrition": {
      const nutritionRows = normalizeNutritionRows(element.nutritionRows);
      const title = element.title || "Nutrition (per 100g)";
      const valueHeader = element.valueHeader || "Value";

      return (
        <div
          style={{
            ...baseStyle,
            display: "grid",
            gridTemplateColumns: "1.3fr 1fr",
            gridTemplateRows: `repeat(${nutritionRows.length + 1}, minmax(0, 1fr))`,
            fontSize: `${element.fontSize || 9}px`,
            fontFamily:
              element.fontFamily || "Poppins, Noto Sans, Arial, sans-serif",
            backgroundColor: "#fff",
          }}
        >
          <div style={{ ...nutritionCellStyle, fontWeight: 700 }}>{title}</div>
          <div style={{ ...nutritionCellStyle, fontWeight: 700 }}>
            {valueHeader}
          </div>
          {nutritionRows.flatMap((row) => [
            <div key={`${row.id}-label`} style={nutritionCellStyle}>
              {row.label}
            </div>,
            <div key={`${row.id}-value`} style={nutritionCellStyle}>
              {resolvePlaceholderText(row.value, dataMap) || "-"}
            </div>,
          ])}
        </div>
      );
    }
    default:
      return null;
  }
};

const nutritionCellStyle = {
  border: "1px solid #333",
  padding: "2px 4px",
  textAlign: "left",
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  display: "flex",
  alignItems: "center",
};

export default ElementRenderer;
